import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Package, Gavel, Heart, User, TrendingUp, Car, ShoppingCart } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Dashboard = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [myBids, setMyBids] = useState([]);
  const [watchlist, setWatchlist] = useState([]);
  const [carRequests, setCarRequests] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
    } else if (user) {
      fetchData();
    }
  }, [user, authLoading, navigate]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [bidsRes, watchlistRes, requestsRes, reservationsRes, paymentsRes] = await Promise.all([
        axios.get(`${API}/bids/my-bids`, { headers, withCredentials: true }),
        axios.get(`${API}/watchlist`, { headers, withCredentials: true }),
        axios.get(`${API}/vehicle-requests/my-requests`, { headers, withCredentials: true }),
        axios.get(`${API}/reservations`, { headers, withCredentials: true }),
        axios.get(`${API}/payments/my`, { headers, withCredentials: true })
      ]);

      setMyBids(bidsRes.data);
      setWatchlist(watchlistRes.data.vehicles || []);
      setCarRequests(requestsRes.data || []);
      setReservations(reservationsRes.data || []);
      setPayments(paymentsRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20">
        <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const winningBids = myBids.filter(bid => bid.is_winning);
  
  // Check if user is a pending dealer
  const isPendingDealer = user?.role === 'dealer' && user?.approval_status === 'pending';
  const isRejectedDealer = user?.role === 'dealer' && user?.approval_status === 'rejected';

  return (
    <div className="min-h-screen pt-20 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-heading font-bold mb-2" data-testid="dashboard-title">Welcome, {user?.name}</h1>
          <p className="text-textSecondary">
            {isPendingDealer || isRejectedDealer ? 'Dealer Account Dashboard' : 'Manage your bids and watchlist'}
          </p>
        </div>

        {/* Pending Dealer Message */}
        {isPendingDealer && (
          <div className="bg-yellow-600/20 border-2 border-yellow-600 rounded-sm p-8 mb-8 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-yellow-600/20 rounded-full mb-4">
              <Package className="w-8 h-8 text-yellow-500" />
            </div>
            <h2 className="text-2xl font-heading font-bold text-yellow-500 mb-3">Account Pending Admin Approval</h2>
            <p className="text-textSecondary mb-4 max-w-2xl mx-auto">
              Your dealer account registration has been received and is currently being reviewed by our admin team. 
              You will not be able to access the Dealer Panel or upload vehicle listings until your account is approved.
            </p>
            <p className="text-sm text-textSecondary">
              This process typically takes 24-48 hours. You will receive an email notification once your account has been reviewed.
            </p>
          </div>
        )}

        {/* Rejected Dealer Message */}
        {isRejectedDealer && (
          <div className="bg-red-600/20 border-2 border-red-600 rounded-sm p-8 mb-8 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-red-600/20 rounded-full mb-4">
              <Package className="w-8 h-8 text-red-500" />
            </div>
            <h2 className="text-2xl font-heading font-bold text-red-500 mb-3">Account Application Rejected</h2>
            <p className="text-textSecondary mb-4 max-w-2xl mx-auto">
              Unfortunately, your dealer account application has been rejected. You cannot access the Dealer Panel or upload vehicle listings at this time.
            </p>
            <p className="text-sm text-textSecondary">
              For more information, please contact our support team at <span className="text-primary">admin@jamohautos.com</span>
            </p>
          </div>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-12">
          <div className="glass p-6 rounded-sm">
            <div className="flex items-center justify-between mb-4">
              <Gavel className="w-8 h-8 text-primary" />
              <TrendingUp className="w-5 h-5 text-green-500" />
            </div>
            <div className="text-3xl font-heading font-bold mb-1">{myBids.length}</div>
            <div className="text-sm text-textSecondary">Total Bids</div>
          </div>

          <div className="glass p-6 rounded-sm">
            <div className="flex items-center justify-between mb-4">
              <ShoppingCart className="w-8 h-8 text-primary" />
              <TrendingUp className="w-5 h-5 text-green-500" />
            </div>
            <div className="text-3xl font-heading font-bold mb-1">{reservations.length}</div>
            <div className="text-sm text-textSecondary">My Reservations</div>
          </div>

          <div className="glass p-6 rounded-sm">
            <div className="flex items-center justify-between mb-4">
              <Heart className="w-8 h-8 text-primary" />
              <TrendingUp className="w-5 h-5 text-green-500" />
            </div>
            <div className="text-3xl font-heading font-bold mb-1">{watchlist.length}</div>
            <div className="text-sm text-textSecondary">Watchlist Items</div>
          </div>

          <div className="glass p-6 rounded-sm">
            <div className="flex items-center justify-between mb-4">
              <User className="w-8 h-8 text-primary" />
            </div>
            <div className="text-lg font-heading font-bold mb-1 capitalize">{user?.role}</div>
            <div className="text-sm text-textSecondary">Account Type</div>
            {(user?.role === 'dealer' || user?.role === 'admin') && (
              <Link
                to="/dealer-dashboard"
                className="mt-3 block bg-primary hover:bg-primaryHover text-white rounded-sm font-bold text-sm px-4 py-2 text-center transition-colors"
              >
                Dealer Dashboard
              </Link>
            )}
          </div>
        </div>

        {/* My Reservations */}
        <div className="mb-12">
          <h2 className="text-2xl font-heading font-bold mb-6">My Reservations</h2>
          {reservations.length === 0 ? (
            <div className="glass p-12 rounded-sm text-center">
              <ShoppingCart className="w-16 h-16 text-textSecondary mx-auto mb-4" />
              <p className="text-textSecondary mb-4">You haven't made any reservations yet</p>
              <Link
                to="/marketplace"
                className="bg-primary hover:bg-primaryHover text-white rounded-sm font-bold px-6 py-3 inline-block transition-colors"
              >
                Browse Vehicles
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {reservations.map((reservation) => {
                const getStatusColor = (status) => {
                  switch(status) {
                    case 'pending': return 'bg-yellow-600';
                    case 'approved': return 'bg-blue-600';
                    case 'rejected': return 'bg-red-600';
                    case 'completed': return 'bg-green-600';
                    default: return 'bg-gray-600';
                  }
                };

                // Check if payment exists for this reservation
                const payment = payments.find(p => p.reservation_id === reservation.reservation_id);
                const hasSuccessfulPayment = payment?.status === 'successful';
                const hasPendingPayment = payment?.status === 'pending';

                return (
                  <div key={reservation.reservation_id} className="glass p-6 rounded-sm">
                    <div className="flex items-center justify-between mb-4">
                      <span className={`px-3 py-1 rounded-sm text-xs font-bold uppercase ${getStatusColor(reservation.status)}`}>
                        {reservation.status}
                      </span>
                      <span className="text-xs text-textSecondary">
                        {new Date(reservation.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    <h3 className="font-heading font-bold text-xl mb-2">
                      {reservation.vehicle_year} {reservation.vehicle_make} {reservation.vehicle_model}
                    </h3>
                    <div className="text-2xl font-heading font-bold text-primary mb-2">
                      ₦{(reservation.reserved_price || reservation.amount || 0).toLocaleString()}
                    </div>

                    {/* Payment Status */}
                    <div className="mb-4">
                      {hasSuccessfulPayment ? (
                        <div className="bg-green-900/20 border border-green-600 rounded-sm p-3">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-green-500 font-bold text-xs uppercase">Deposit Paid</span>
                            <span className="text-green-500 font-bold">₦{payment.amount.toLocaleString()}</span>
                          </div>
                          <div className="text-xs text-textSecondary">
                            Paid on {new Date(payment.created_at).toLocaleDateString()}
                          </div>
                          {payment.paystack_reference && (
                            <div className="text-xs text-textSecondary font-mono mt-1">
                              Ref: {payment.paystack_reference}
                            </div>
                          )}
                        </div>
                      ) : hasPendingPayment ? (
                        <div className="bg-yellow-900/20 border border-yellow-600 rounded-sm p-3">
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse"></div>
                            <span className="text-yellow-500 font-bold text-xs uppercase">Payment Pending</span>
                          </div>
                          <div className="text-xs text-textSecondary mt-1">
                            Processing... Check back soon
                          </div>
                        </div>
                      ) : payment?.status === 'failed' ? (
                        <div className="bg-red-900/20 border border-red-600 rounded-sm p-3">
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                            <span className="text-red-500 font-bold text-xs uppercase">Payment Failed</span>
                          </div>
                          <div className="text-xs text-textSecondary mt-1">
                            Please try again
                          </div>
                        </div>
                      ) : (
                        <div className="bg-red-900/20 border border-red-600 rounded-sm p-3">
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                            <span className="text-red-500 font-bold text-xs uppercase">Payment Required</span>
                          </div>
                          <div className="text-xs text-textSecondary mt-1">
                            Deposit needed to secure vehicle
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="flex gap-2">
                      {!hasSuccessfulPayment && (
                        <Link
                          to={`/vehicle/${reservation.vehicle_id}`}
                          className="flex-1 bg-primary hover:bg-primaryHover text-white rounded-sm font-bold px-4 py-2 text-center transition-colors"
                        >
                          Pay Deposit
                        </Link>
                      )}
                      <Link
                        to={`/vehicle/${reservation.vehicle_id}`}
                        className={`${hasSuccessfulPayment ? 'flex-1' : 'flex-1'} bg-white/5 hover:bg-white/10 text-white rounded-sm font-bold px-4 py-2 text-center transition-colors`}
                      >
                        View
                      </Link>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* My Payments */}
        <div className="mb-12">
          <h2 className="text-2xl font-heading font-bold mb-6">My Payments</h2>
          {payments.length === 0 ? (
            <div className="glass p-12 rounded-sm text-center">
              <Package className="w-16 h-16 text-textSecondary mx-auto mb-4" />
              <p className="text-textSecondary">No payments yet</p>
            </div>
          ) : (
            <div className="glass rounded-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-surfaceHighlight">
                    <tr>
                      <th className="p-4 text-left font-heading text-sm">Vehicle</th>
                      <th className="p-4 text-left font-heading text-sm">Deposit Paid</th>
                      <th className="p-4 text-left font-heading text-sm">Status</th>
                      <th className="p-4 text-left font-heading text-sm">Reference</th>
                      <th className="p-4 text-left font-heading text-sm">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payments.map((payment) => {
                      const getPaymentStatusColor = (status) => {
                        switch(status) {
                          case 'successful': return 'bg-green-600';
                          case 'pending': return 'bg-yellow-600';
                          case 'failed': return 'bg-red-600';
                          default: return 'bg-gray-600';
                        }
                      };

                      return (
                        <tr key={payment.payment_id} className="border-t border-white/10 hover:bg-surfaceHighlight transition-colors">
                          <td className="p-4">
                            <div className="font-bold">
                              {payment.vehicle_details?.year} {payment.vehicle_details?.make} {payment.vehicle_details?.model}
                            </div>
                          </td>
                          <td className="p-4">
                            <div className="font-bold text-primary">
                              ₦{payment.amount.toLocaleString()}
                            </div>
                          </td>
                          <td className="p-4">
                            <span className={`px-3 py-1 rounded-sm text-xs font-bold uppercase ${getPaymentStatusColor(payment.status)}`}>
                              {payment.status}
                            </span>
                          </td>
                          <td className="p-4">
                            <div className="text-sm font-mono text-textSecondary">
                              {payment.paystack_reference}
                            </div>
                          </td>
                          <td className="p-4 text-sm text-textSecondary">
                            {new Date(payment.created_at).toLocaleDateString()}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* My Active Bids */}
        <div className="mb-12">
          <h2 className="text-2xl font-heading font-bold mb-6" data-testid="my-bids-section">My Active Bids</h2>
          {myBids.length === 0 ? (
            <div className="glass p-12 rounded-sm text-center">
              <p className="text-textSecondary mb-4">You haven't placed any bids yet</p>
              <Link
                to="/marketplace"
                className="bg-primary hover:bg-primaryHover text-white rounded-sm font-bold px-6 py-3 inline-block transition-colors"
              >
                Browse Vehicles
              </Link>
            </div>
          ) : (
            <div className="space-y-4">
              {myBids.map((bid) => (
                <div
                  key={bid.bid_id}
                  data-testid={`bid-item-${bid.bid_id}`}
                  className="glass p-6 rounded-sm hover:border-primary/50 transition-colors"
                >
                  <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                    <div className="flex items-center space-x-4 flex-1">
                      {bid.vehicle?.images?.[0] && (
                        <img
                          src={bid.vehicle.images[0]}
                          alt="Vehicle"
                          className="w-24 h-16 object-cover rounded-sm"
                        />
                      )}
                      <div>
                        <h3 className="font-heading font-bold text-lg">
                          {bid.vehicle?.year} {bid.vehicle?.make} {bid.vehicle?.model}
                        </h3>
                        <p className="text-sm text-textSecondary">
                          Bid: ${bid.amount.toLocaleString()} • Current: ${bid.auction?.current_bid.toLocaleString()}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-4">
                      {bid.is_winning ? (
                        <span className="px-4 py-2 bg-green-600 rounded-sm text-sm font-bold uppercase">Winning</span>
                      ) : (
                        <span className="px-4 py-2 bg-yellow-600 rounded-sm text-sm font-bold uppercase">Outbid</span>
                      )}
                      <Link
                        to={`/vehicle/${bid.vehicle?.vehicle_id}`}
                        className="bg-primary hover:bg-primaryHover text-white rounded-sm font-bold px-4 py-2 transition-colors"
                      >
                        View
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* My Car Requests */}
        <div className="mb-12">
          <h2 className="text-2xl font-heading font-bold mb-6">My Car Requests</h2>
          {carRequests.length === 0 ? (
            <div className="glass p-12 rounded-sm text-center">
              <Car className="w-16 h-16 mx-auto mb-4 text-textSecondary" />
              <p className="text-textSecondary text-lg mb-4">You haven't submitted any car requests yet</p>
              <Link
                to="/request-vehicle"
                className="bg-primary hover:bg-primaryHover text-white rounded-sm font-bold px-6 py-3 inline-block transition-colors"
              >
                Request a Vehicle
              </Link>
            </div>
          ) : (
            <div className="space-y-4">
              {carRequests.map((request) => (
                <div key={request.request_id} className="glass p-6 rounded-sm">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="font-heading font-bold text-xl mb-2">
                        {request.make} {request.model} ({request.year_min} - {request.year_max})
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                        <div>
                          <span className="text-textSecondary">Budget:</span>
                          <span className="ml-2 font-bold text-primary">${request.budget?.toLocaleString()}</span>
                        </div>
                        <div>
                          <span className="text-textSecondary">Location:</span>
                          <span className="ml-2">{request.location}</span>
                        </div>
                        <div>
                          <span className="text-textSecondary">Submitted:</span>
                          <span className="ml-2">{new Date(request.created_at).toLocaleDateString()}</span>
                        </div>
                      </div>
                      {request.additional_notes && (
                        <div className="mt-3 pt-3 border-t border-white/10">
                          <p className="text-sm text-textSecondary">
                            <strong>Notes:</strong> {request.additional_notes}
                          </p>
                        </div>
                      )}
                    </div>
                    <div className="ml-4">
                      <span
                        className={`px-4 py-2 rounded-sm text-sm font-bold uppercase ${
                          request.status === 'pending'
                            ? 'bg-yellow-600'
                            : request.status === 'contacted'
                            ? 'bg-blue-600'
                            : 'bg-green-600'
                        }`}
                      >
                        {request.status}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Watchlist */}
        <div>
          <h2 className="text-2xl font-heading font-bold mb-6" data-testid="watchlist-section">Watchlist</h2>
          {watchlist.length === 0 ? (
            <div className="glass p-12 rounded-sm text-center">
              <Heart className="w-16 h-16 mx-auto mb-4 text-textSecondary" />
              <p className="text-textSecondary text-lg mb-4">Your watchlist is empty</p>
              <Link
                to="/marketplace"
                className="bg-primary hover:bg-primaryHover text-white rounded-sm font-bold px-6 py-3 inline-block transition-colors"
              >
                Add Vehicles
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {watchlist.map((vehicle) => (
                <div key={vehicle.vehicle_id} className="glass rounded-sm overflow-hidden group relative">
                  <Link to={`/vehicle/${vehicle.vehicle_id}`} className="block">
                    {vehicle.images?.[0] && (
                      <div className="relative aspect-video overflow-hidden">
                        <img
                          src={vehicle.images[0]?.startsWith('/api') 
                            ? `${process.env.REACT_APP_BACKEND_URL}${vehicle.images[0]}`
                            : vehicle.images[0]
                          }
                          alt="Vehicle"
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                        <div className={`absolute top-3 left-3 px-2 py-1 rounded-sm text-xs font-bold ${
                          vehicle.listing_type === 'direct_sale' ? 'bg-blue-600' : 'bg-primary'
                        }`}>
                          {vehicle.listing_type === 'direct_sale' ? 'FOR SALE' : 'AUCTION'}
                        </div>
                      </div>
                    )}
                    <div className="p-4">
                      <h3 className="font-heading font-bold text-lg mb-2">
                        {vehicle.year} {vehicle.make} {vehicle.model}
                      </h3>
                      <div className="mb-2">
                        {vehicle.listing_type === 'direct_sale' ? (
                          <>
                            <div className="text-xs text-textSecondary">Sale Price</div>
                            <div className="text-xl font-heading font-bold text-primary">
                              {vehicle.currency === 'NGN' ? '₦' : '$'}{vehicle.price?.toLocaleString()}
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="text-xs text-textSecondary">Current Bid</div>
                            <div className="text-xl font-heading font-bold text-primary">
                              ${vehicle.auction?.current_bid?.toLocaleString() || 'N/A'}
                            </div>
                          </>
                        )}
                      </div>
                      <p className="text-textSecondary text-sm flex items-center">
                        <Package className="w-4 h-4 mr-1" />
                        {vehicle.location}
                      </p>
                    </div>
                  </Link>
                  <button
                    onClick={async (e) => {
                      e.preventDefault();
                      try {
                        const token = localStorage.getItem('token');
                        await axios.delete(`${API}/watchlist/${vehicle.vehicle_id}`, {
                          headers: { Authorization: `Bearer ${token}` },
                          withCredentials: true
                        });
                        fetchData();
                      } catch (error) {
                        console.error('Error removing from watchlist:', error);
                      }
                    }}
                    className="absolute top-3 right-3 p-2 bg-black/50 backdrop-blur-sm hover:bg-red-600/80 rounded-full transition-colors z-10"
                    title="Remove from watchlist"
                  >
                    <Heart className="w-5 h-5 fill-primary text-primary" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;